<template>
    <LoadingComponent :props="loading" />
    <div class="row">
        <div class="col-12 lg:col-8">
            <!-- Cintillo de Advertencia de Disponibilidad y Stock Sujeto a Verificación (Solo visible en modo offline) -->
            <div v-if="isOffline" class="mb-4 p-4 rounded-lg border border-amber-500 bg-amber-100 flex items-center gap-3 shadow-sm">
                <i class="fa-solid fa-circle-info text-xl text-amber-600 mt-0.5 shrink-0"></i>
                <div class="text-xs sm:text-sm leading-relaxed font-normal text-amber-600">
                    <strong class="font-semibold block text-sm sm:text-base text-amber-800">Aviso sobre disponibilidad y existencias:</strong>
                    Los pedidos en modo Offline (fuera de línea) o procesados en momentos de alta demanda y actualización de inventario, están sujetos a confirmación final de stock al ingresar a preparación. Si algún producto llegase a encontrarse fuera de inventario y no pueda ser resurtido, nuestro equipo de soporte le informará de inmediato y la orden podrá ser cancelada sin penalización alguna.
                </div>
            </div>

            <div class="border rounded-lg p-4 mb-6">
                <h4 class="font-bold capitalize pt-1 pb-4 border-b">
                    {{ $t('label.select_payment_method') }}
                </h4>

                <div class="grid grid-cols-2 sm:grid-cols-5 gap-4 py-4">
                    <div v-if="setting.site_online_payment_gateway === ActivityEnum.ENABLE"
                        v-for="paymentGateway in paymentGateways" @click.prevent="selectPaymentMethod(paymentGateway)"
                        :class="Object.keys(paymentMethod).length > 0 && paymentGateway.id === paymentMethod.id ? 'bg-gray-100 p-3' : 'bg-white p-1'"
                        class="flex flex-col items-center justify-center gap-2.5 cursor-pointer rounded-lg">
                        <img class="h-auto border rounded-md" :src="paymentGateway.image" alt="payment" />
                        <span class="text-xs font-medium">{{ paymentGateway.name }}</span>
                    </div>
                    <div v-if="Object.keys(cashOnDelivery).length > 0 && setting.site_cash_on_delivery === ActivityEnum.ENABLE"
                        @click.prevent="selectPaymentMethod(cashOnDelivery)"
                        :class="Object.keys(paymentMethod).length > 0 && cashOnDelivery.id === paymentMethod.id ? 'bg-gray-100 p-3' : 'bg-white p-1'"
                        class="flex flex-col items-center justify-center gap-2.5 cursor-pointer rounded-lg">
                        <img class="h-auto border rounded-md" :src="cashOnDelivery.image" alt="payment" />
                        <span class="text-xs font-medium">{{ cashOnDelivery.name }}</span>
                    </div>
                    <div v-if="profile.balance >= total" @click.prevent="selectPaymentMethod(credit)"
                        :class="Object.keys(paymentMethod).length > 0 && credit.id === paymentMethod.id ? 'bg-gray-100 p-3' : 'bg-white p-1'"
                        class="flex flex-col items-center justify-center gap-2.5 cursor-pointer rounded-lg">
                        <img class="h-auto border rounded-md" :src="credit.image" alt="payment" />
                        <span class="text-xs font-medium">
                            {{ credit.name }} 
                            <!-- ({{ profile.balance }}) -->
                        </span>
                    </div>
                </div>

                <div v-if="isTransferenciaSelected" class="rounded-md border px-3 py-2 text-sm">
                    {{ $t('message.transferencia_checkout_notice') }}
                </div>
                <div v-if="isStripeSelected" class="rounded-md border px-3 py-2 text-sm">
                    {{ $t('message.stripe_checkout_notice') }}
                </div>
                <div v-if="isCreditSelected" class="rounded-md border px-3 py-2 text-sm">
                    {{ $t('message.credit_checkout_notice') }}
                </div>
                <div v-if="isPaypalSelected" class="rounded-md border px-3 py-2 text-sm">
                    {{ $t('message.paypal_checkout_notice') }}
                </div>
                <div v-if="isCodSelected" class="rounded-md border px-3 py-2 text-sm">
                    {{ $t('message.cod_checkout_notice') }}
                </div>
            </div>

            <div v-if="showShippingQuoteSelector" class="mb-6 rounded-2xl">
                <h4 class="font-bold capitalize p-4 border-b ">
                    {{ $t('label.select_shipping_quote') }}
                </h4>
                <div class="p-4 grid gap-3">
                    <label v-for="quote in shippingQuotes" :key="quote.id"
                        class="flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer"
                        :class="selectedShippingQuote && selectedShippingQuote.id === quote.id ? 'border-primary/50 bg-primary/5' : 'border-[#F1F1F1]'">
                        <div class="flex items-center gap-2">
                            <input type="radio" name="shipping_quote" :value="quote.id"
                                :checked="selectedShippingQuote && selectedShippingQuote.id === quote.id"
                                @change="selectShippingQuote(quote)">
                            <span class="text-sm">
                                {{ quote.provider }}
                                <span v-if="quote.service_level"> - {{ quote.service_level }}</span>
                                <span v-if="quote.days"> ({{ quote.days }})</span>
                            </span>
                        </div>
                        <span class="text-sm font-semibold">
                            {{ currencyFormat(quote.amount, setting.site_digit_after_decimal_point, setting.site_default_currency_symbol, setting.site_currency_position) }}
                        </span>
                    </label>
                </div>
            </div>

            <div class="max-lg:hidden flex items-center justify-start gap-3">
                <router-link :to="{ name: 'frontend.checkout.checkout' }"
                    :class="isQuoteCalculating ? 'pointer-events-none opacity-50' : ''"
                    class="field-button w-fit font-semibold tracking-wide normal-case text-secondary bg-[#E5E5EB]">
                    {{ $t('button.back_to_delivery_method') }}
                </router-link>

                <button type="button" v-if="setting.whatsapp_status === ActivityEnum.ENABLE"
                    :disabled="isQuoteCalculating"
                    :class="isQuoteCalculating ? 'disabled:opacity-50 disabled:bg-gray-400' : ''"
                    class="flex items-center gap-2 field-button w-fit font-semibold tracking-wide normal-case text-white bg-[#1AB759]"
                    @click.prevent="confirmOrder">
                    <i class="lab lab-whatsapp text-2xl"></i>
                    {{ $t('button.proceed_to_whatsapp') }}
                </button>

                <button v-else @click.prevent="confirmOrder"
                    :disabled="isQuoteCalculating"
                    :class="isQuoteCalculating ? 'disabled:opacity-50 disabled:bg-gray-400' : ''"
                    class="field-button w-fit font-semibold tracking-wide normal-case">
                    {{ $t('button.confirm_order') }}
                </button>
            </div>
        </div>

        <div class="col-12 lg:col-4">
            <CouponComponent v-if="coupons.length > 0" />
            <SummeryComponent />

            <div class="max-lg:flex hidden flex-col-reverse sm:flex-row items-center justify-between gap-5 mt-10">
                <router-link :to="{ name: 'frontend.checkout.checkout' }"
                    :class="isQuoteCalculating ? 'pointer-events-none opacity-50' : ''"
                    class="field-button font-semibold tracking-wide normal-case text-secondary bg-[#E5E5EB]">
                    {{ $t('button.back') }}
                </router-link>

                <button type="button" v-if="setting.whatsapp_status === ActivityEnum.ENABLE"
                    :disabled="isQuoteCalculating"
                    :class="isQuoteCalculating ? 'disabled:opacity-50 disabled:bg-gray-400' : ''"
                    class="field-button font-semibold tracking-wide normal-case text-white bg-[#1AB759]"
                    @click.prevent="confirmOrder($event)">
                    <i class="lab lab-whatsapp text-sm"></i>
                    {{ $t('button.proceed_to_whatsapp') }}
                </button>

                <button v-else @click.prevent="confirmOrder($event)"
                    :disabled="isQuoteCalculating"
                    :class="isQuoteCalculating ? 'disabled:opacity-50 disabled:bg-gray-400' : ''"
                    class="field-button font-semibold tracking-wide normal-case">
                    {{ $t('button.confirm_order') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import statusEnum from "../../../../enums/modules/statusEnum";
import SummeryComponent from "../SummeryComponent.vue";
import CouponComponent from "../CouponComponent.vue";
import LoadingComponent from "../../components/LoadingComponent.vue";
import _ from "lodash";
import alertService from "../../../../services/alertService";
import sourceEnum from "../../../../enums/modules/sourceEnum";
import ENV from "../../../../config/env";
import ActivityEnum from "../../../../enums/modules/activityEnum";
import shippingMethodEnum from "../../../../enums/modules/shippingMethodEnum";
import orderTypeEnum from "../../../../enums/modules/orderTypeEnum";
import skydropxRateModeEnum from "../../../../enums/modules/skydropxRateModeEnum";
import appService from "../../../../services/appService";

export default {
    name: "PaymentComponent",
    components: { CouponComponent, SummeryComponent, LoadingComponent },
    data() {
        return {
            loading: {
                isActive: false
            },
            paymentGateways: [],
            credit: {},
            cashOnDelivery: {},
            statusEnum: statusEnum,
            sourceEnum: sourceEnum,
            ActivityEnum: ActivityEnum,
            skydropxRateModeEnum: skydropxRateModeEnum,
            form: {}
        }
    },
    computed: {
        coupons: function () {
            return this.$store.getters['frontendCoupon/lists'];
        },
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        profile: function () {
            return this.$store.getters.authInfo;
        },
        paymentMethod: function () {
            return this.$store.getters['frontendCart/paymentMethod'];
        },
        subtotal: function () {
            return this.$store.getters['frontendCart/subtotal'];
        },
        discount: function () {
            return this.$store.getters['frontendCart/discount'];
        },
        total: function () {
            return this.$store.getters['frontendCart/total'];
        },
        orderType: function () {
            return this.$store.getters['frontendCart/orderType'];
        },
        getShippingAddress: function () {
            return this.$store.getters['frontendCart/shippingAddress'];
        },
        getBillingAddress: function () {
            return this.$store.getters['frontendCart/billingAddress'];
        },
        getOutletAddress: function () {
            return this.$store.getters['frontendCart/outletAddress'];
        },
        cartCoupon: function () {
            return this.$store.getters['frontendCart/coupon'];
        },
        products: function () {
            return this.$store.getters['frontendCart/activeLists'];
        },
        shippingCharge: function () {
            return this.$store.getters['frontendCart/shippingCharge']
        },
        totalTax: function () {
            return this.$store.getters['frontendCart/totalTax'];
        },
        shippingQuotes: function () {
            return this.$store.getters['frontendCart/shippingQuotes'];
        },
        shippingRateMode: function () {
            return this.$store.getters['frontendCart/shippingRateMode'];
        },
        selectedShippingQuote: function () {
            return this.$store.getters['frontendCart/selectedShippingQuote'];
        },
        showShippingQuoteSelector: function () {
            return (
                this.setting.shipping_setup_method === shippingMethodEnum.SKYDROPX ||
                this.setting.shipping_setup_method === shippingMethodEnum.HYBRID
            )
                && this.orderType === orderTypeEnum.DELIVERY
                && this.shippingRateMode === skydropxRateModeEnum.CUSTOMER_SELECT
                && this.shippingQuotes.length > 0;
        },
        isTransferenciaSelected: function () {
            return this.paymentMethod && this.paymentMethod.slug === 'transferencia';
        },
        isStripeSelected: function () {
            return this.paymentMethod && this.paymentMethod.slug === 'stripe';
        },
        isPaypalSelected: function () {
            return this.paymentMethod && this.paymentMethod.slug === 'paypal';
        },
        isCodSelected: function () {
            return this.paymentMethod && this.paymentMethod.slug === 'cashondelivery';
        },
        isCreditSelected: function () {
            return this.paymentMethod && this.paymentMethod.slug === 'credit';
        },
        isQuoteCalculating: function () {
            return this.$store.getters['frontendCart/quoteCalculating'];
        },
        isOffline: function () {
            const isStoreOnline = this.$store.getters['offlineSync/isOnline'];
            return isStoreOnline === false || !navigator.onLine;
        }
    },
    mounted() {
        this.loading.isActive = true;
        this.$store.dispatch('frontendPaymentGateway/lists', { status: this.statusEnum.ACTIVE }).then(res => {
            if (res.data.data.length > 0) {
                _.forEach(res.data.data, (gateway) => {
                    // Determinar si el gateway está habilitado según sus opciones (ej. transferencia_status, paypal_status)
                    let statusOpt = (gateway.options || []).find(o => (o.option === (gateway.slug + '_status')) || (o.option && o.option.endsWith && o.option.endsWith('_status')));
                    let isEnabled = true;
                    if (typeof statusOpt !== 'undefined') {
                        isEnabled = parseInt(statusOpt.value) === this.ActivityEnum.ENABLE;
                    }

                    if (!isEnabled) {
                        return; // omitir gateways deshabilitados por opción
                    }

                    if (gateway.slug === "credit") {
                        this.credit = gateway;
                    } else if (gateway.slug === "cashondelivery") {
                        this.cashOnDelivery = gateway;
                        // if (this.setting.site_cash_on_delivery === this.ActivityEnum.ENABLE) {
                        //     this.selectPaymentMethod(this.cashOnDelivery);
                        // }
                    } else {
                        this.paymentGateways.push(gateway);
                        if (gateway.slug === "stripe") {
                            this.selectPaymentMethod(gateway);
                        }
                    }
                });
            }
            this.loading.isActive = false;
        }).catch((err) => {
            this.loading.isActive = false;
        });

        if (
            (this.setting.shipping_setup_method === shippingMethodEnum.SKYDROPX || this.setting.shipping_setup_method === shippingMethodEnum.HYBRID)
            && this.orderType === orderTypeEnum.DELIVERY
        ) {
            this.$store.dispatch('frontendCart/quoteShippingCharge', { silent: false }).then().catch((err) => {
                if (err.response?.data?.message) {
                    alertService.error(err.response.data.message);
                }
            });
        }
    },
    methods: {
        currencyFormat(amount, decimal, currency, position) {
            return appService.currencyFormat(amount, decimal, currency, position);
        },
        selectPaymentMethod: function (paymentMethod) {
            this.$store.dispatch("frontendCart/paymentMethod", paymentMethod);
        },
        selectShippingQuote: function (quote) {
            this.$store.dispatch('frontendCart/selectShippingQuote', quote).then().catch();
        },
        confirmOrder: function (e) {
            e.target.disabled = true;
            const isSkydropxDelivery =
                (this.setting.shipping_setup_method === shippingMethodEnum.SKYDROPX || this.setting.shipping_setup_method === shippingMethodEnum.HYBRID)
                && this.orderType === orderTypeEnum.DELIVERY;

            const proceedOrder = () => {
                this.form = {
                    subtotal: this.subtotal,
                    discount: this.discount,
                    tax: this.totalTax,
                    shipping_charge: this.shippingCharge,
                    total: this.total,
                    order_type: this.orderType,
                    shipping_id: Object.keys(this.getShippingAddress).length > 0 ? this.getShippingAddress.id : 0,
                    billing_id: Object.keys(this.getBillingAddress).length > 0 ? this.getBillingAddress.id : 0,
                    outlet_id: Object.keys(this.getOutletAddress).length > 0 ? this.getOutletAddress.id : 0,
                    shipping_quote_id: this.selectedShippingQuote ? this.selectedShippingQuote.id : null,
                    coupon_id: Object.keys(this.cartCoupon).length > 0 ? this.cartCoupon.id : 0,
                    source: sourceEnum.WEB,
                    payment_method: Object.keys(this.paymentMethod).length > 0 ? this.paymentMethod.id : 0,
                    products: JSON.stringify(this.products)
                }

                this.$store.dispatch('frontendOrder/save', this.form).then(orderResponse => {
                    this.loading.isActive = false;
                    let paymentSlug = Object.keys(this.paymentMethod).length > 0 ? this.paymentMethod.slug : '';
                    if (paymentSlug) {
                        window.location.href = ENV.API_URL + "/payment/" + paymentSlug + "/pay/" + orderResponse.data.data.id;
                    } else {
                        e.target.disabled = false;
                        alertService.error(this.$t('message.payment_method_required'));
                    }
                }).catch((err) => {
                    this.loading.isActive = false;
                    e.target.disabled = false;
                    if (typeof err.response?.data?.errors === 'object') {
                        _.forEach(err.response.data.errors, (error) => {
                            alertService.error(error[0]);
                        });
                    } else if (err.response?.data?.message) {
                        alertService.error(err.response.data.message);
                    }
                });
            };

            if (isSkydropxDelivery) {
                this.$store.dispatch('frontendCart/quoteShippingCharge', { silent: false }).then(() => {
                    if (this.shippingRateMode === skydropxRateModeEnum.CUSTOMER_SELECT && !this.selectedShippingQuote) {
                        e.target.disabled = false;
                        alertService.error(this.$t('message.select_shipping_quote'));
                        return;
                    }
                    proceedOrder();
                }).catch((err) => {
                    e.target.disabled = false;
                    if (err.response?.data?.message) {
                        alertService.error(err.response.data.message);
                    }
                });
                return;
            }

            proceedOrder();
        }
    }
}
</script>